import { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import validator from 'validator';
import axiosInstance, { refreshAccessToken } from "../utils/axios";
import { showToast } from "../components/reusables/CommonFunction";
import MESSAGES from "../utils/Constants";

const AuthContext = createContext();
export default AuthContext;

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const [pageLoading, setPageLoading] = useState(true);
    const [authTokens, setAuthTokens] = useState(() => {
        const tokens = localStorage.getItem("AuthTokens");
        return tokens ? JSON.parse(tokens) : null;
    });
    const [userDetails, setUserDetails] = useState(() => {
        return authTokens ? jwtDecode(authTokens.access) : null;
    });
    const [clinicBranchDetails, setClinicBranchDetails] = useState(() => {
        const details = localStorage.getItem("clinicBranchDetails");
        return details ? JSON.parse(details) : null;
    });

    const onPressSignIn = async (e, signInState, setSignInState, setSignInErrors) => {
        e.preventDefault();
        const newSignInErrors = {};
        const { email_id, password } = signInState;

        if (!email_id) newSignInErrors.email_id = MESSAGES.ENTER_EMAIL_ADDRESS;
        if (!password) newSignInErrors.password = MESSAGES.ENTER_PASSWORD;

        if (Object.keys(newSignInErrors).length > 0) {
            setSignInErrors(newSignInErrors);
            return;
        }

        if (!validator.isEmail(email_id)) {
            setSignInErrors({
                email_id: MESSAGES.ENTER_VALID_EMAIL_ADDRESS,
                password: "",
            });
            return;
        }

        try {
            const response = await axiosInstance.post("/token/", { email_id, password }, { skipAuth: true });
            if (response.status === 200) {
                const accessToken = jwtDecode(response.data.access);
                onFetchClinicBranchDetails(accessToken.clinic_id);
                setAuthTokens(response.data);
                setUserDetails(accessToken);
                localStorage.setItem("AuthTokens", JSON.stringify(response.data));
            }
        } catch (error) {
            showToast("error", error.response?.data?.detail || "Network error. Please try again later.");
        } finally {
            setSignInState({ email_id: "", password: "" });
        }
    };

    const onFetchClinicBranchDetails = async (clinic_id) => {
        try {
            const response = await axiosInstance.get("/clinicbranches/clinic_branch_details/", {
                params: {
                    clinicId: clinic_id
                }
            });
            if (response.status === 200) {
                localStorage.setItem("clinicBranchDetails", JSON.stringify(response.data.data));
                setClinicBranchDetails(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching clinic branch details:", error);
        }
    }

    // Function to log out the user
    const onPressLogout = useCallback(async () => {
        try {
            const authTokens = JSON.parse(localStorage.getItem("AuthTokens"));
            if (authTokens?.refresh) {
                await axiosInstance.post("/logout/", { refresh: authTokens.refresh },
                    {
                        headers: {
                            Authorization: `Bearer ${authTokens.access}`,
                        },
                    }
                );
            }
        } catch (error) {
            console.error("Logout API failed", error);
        } finally {
            localStorage.removeItem("AuthTokens");
            setAuthTokens(null);
            setUserDetails(null);
            navigate("/sign-in", { replace: true });
        }
    }, [navigate]);

    // Function to refresh the access token
    const handleRefreshToken = useCallback(async () => {
        try {
            const newTokens = await refreshAccessToken(); // Call from axios.jsx
            const accessToken = jwtDecode(newTokens.access);
            setAuthTokens(newTokens);
            setUserDetails(accessToken);
        } catch (error) {
            console.error("Error refreshing token:", error);
            onPressLogout();
        }
    }, [onPressLogout]);

    // Initialize auth context
    useEffect(() => {
        const initializeAuth = async () => {
            if (authTokens && authTokens.refresh) {
                await handleRefreshToken();
            }
            setPageLoading(false);
        };

        if (pageLoading) {
            initializeAuth();
        }
    }, [authTokens, pageLoading, handleRefreshToken]);

    // Proactively refresh token every 13 minutes
    useEffect(() => {
        if (!authTokens) return;

        const interval = setInterval(() => {
            handleRefreshToken();
        }, 13 * 60 * 1000);

        return () => clearInterval(interval);
    }, [authTokens, handleRefreshToken]);

    return (
        <AuthContext.Provider value={{ authTokens, userDetails, clinicBranchDetails, onPressSignIn, onPressLogout }}>
            {!pageLoading && children}
        </AuthContext.Provider>
    );
};